// clubs/data.js — club names and URLs, and the numbers behind the club pages
// (/clubs and /clubs/:slug). Teams are identified by their stored name (the
// football-data shortName, e.g. "Man United"); clubs.json maps each to a full
// name and slug.

import CLUBS from "./clubs.json";
import { displayName, seasonStartYear } from "../heatmap/utils";

export const slugify = (s) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// { team, name: "Manchester United", short: "Man United", slug }
export function clubInfo(team) {
  const c = CLUBS[team];
  return { team, name: c?.name || team, short: displayName(team), slug: c?.slug || slugify(team) };
}
export const clubPath = (team) => `/clubs/${clubInfo(team).slug}`;
// "Arsenal's", "Wolves'".
export const possessive = (name) => (name.endsWith("s") ? `${name}'` : `${name}'s`);
export const teamQuery = (team) => `team=${encodeURIComponent(team)}`;
export const seasonText = (startYear) => `${startYear}/${String((startYear + 1) % 100).padStart(2, "0")}`;
export const seasonIdStart = (seasonId) => 2000 + Number(String(seasonId).slice(0, 2)) - (Number(String(seasonId).slice(0, 2)) > 80 ? 100 : 0);

// Points deducted by the Premier League, by club and season start year.
// Points are 3 for a win and 1 for a draw (every PL season), minus these.
// Add any new deduction here: it feeds the all-time table and club pages.
export const POINT_DEDUCTIONS = [
  { team: "Middlesbrough", season: 1996, points: 3, reason: "failing to fulfil a fixture" },
  { team: "Portsmouth", season: 2009, points: 9, reason: "entering administration" },
  { team: "Everton", season: 2023, points: 8, reason: "breaching profit and sustainability rules" },
  { team: "Nottingham", season: 2023, points: 4, reason: "breaching profit and sustainability rules" },
];
export const deductionFor = (team, season) =>
  POINT_DEDUCTIONS.filter((d) => d.team === team && (season === undefined || d.season === season)).reduce((n, d) => n + d.points, 0);
export const pointsFor = (team, won, drawn, season) => 3 * won + drawn - deductionFor(team, season);

// "3–1 win" / "1–1 draw" / "0–2 defeat", from the club's side.
export const resultLabel = (gf, ga) => `${gf}–${ga} ${gf > ga ? "win" : gf < ga ? "defeat" : "draw"}`;
export const scorelineKey = (gf, ga) => `${Math.max(gf, ga)}-${Math.min(gf, ga)}`;

// Everything a club page shows, from `items` (the API's ?team= list, oldest
// first) seen from `team`'s side.
export function clubStats(team, items) {
  const matches = items.map((m) => {
    const home = m.homeTeam === team;
    const gf = Number(home ? m.homeTeamScore : m.awayTeamScore);
    const ga = Number(home ? m.awayTeamScore : m.homeTeamScore);
    return { ...m, home, gf, ga, opponent: home ? m.awayTeam : m.homeTeam, season: seasonStartYear(m.utcDate) };
  });

  const totals = { played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0 };
  const cells = new Map(); // "gf-ga" -> { gf, ga, count, first, last }
  const seasons = new Map(); // start year -> totals
  const opponents = new Map(); // team -> { played, won, drawn, lost }
  const firsts = []; // matches that gave the club a result it had never had
  for (const m of matches) {
    const res = m.gf > m.ga ? "won" : m.gf < m.ga ? "lost" : "drawn";
    totals.played++;
    totals[res]++;
    totals.gf += m.gf;
    totals.ga += m.ga;

    const key = `${m.gf}-${m.ga}`;
    const cell = cells.get(key);
    if (cell) {
      cell.count++;
      cell.last = m;
    } else {
      cells.set(key, { key, gf: m.gf, ga: m.ga, count: 1, first: m, last: m });
      firsts.push(m);
    }

    const s = seasons.get(m.season) || { season: m.season, played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0 };
    s.played++;
    s[res]++;
    s.gf += m.gf;
    s.ga += m.ga;
    seasons.set(m.season, s);

    const o = opponents.get(m.opponent) || { team: m.opponent, played: 0, won: 0, drawn: 0, lost: 0 };
    o.played++;
    o[res]++;
    opponents.set(m.opponent, o);
  }

  const byDateDesc = (a, b) => b.utcDate.localeCompare(a.utcDate);
  const margin = (m) => Math.abs(m.gf - m.ga);
  const goals = (m) => m.gf + m.ga;
  const cellList = [...cells.values()];
  return {
    matches,
    totals,
    cells,
    maxCount: Math.max(1, ...cellList.map((c) => c.count)),
    maxGoals: Math.max(5, ...matches.map((m) => Math.max(m.gf, m.ga))),
    oneOffs: cellList.filter((c) => c.count === 1).map((c) => c.first).sort(byDateDesc),
    lastFirst: firsts[firsts.length - 1] || null,
    biggestWins: matches.filter((m) => m.gf > m.ga).sort((a, b) => margin(b) - margin(a) || b.gf - a.gf || a.utcDate.localeCompare(b.utcDate)).slice(0, 5),
    heaviestDefeats: matches.filter((m) => m.gf < m.ga).sort((a, b) => margin(b) - margin(a) || b.ga - a.ga || a.utcDate.localeCompare(b.utcDate)).slice(0, 5),
    highestScoring: [...matches].sort((a, b) => goals(b) - goals(a) || a.utcDate.localeCompare(b.utcDate)).slice(0, 5),
    common: [...cellList].sort((a, b) => b.count - a.count || a.key.localeCompare(b.key)).slice(0, 6),
    seasons: [...seasons.values()].sort((a, b) => b.season - a.season),
    opponents: [...opponents.values()].sort((a, b) => b.played - a.played || a.team.localeCompare(b.team)),
  };
}
